package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.requests.AbsenceDecision
import com.evoapps.evoshift.domain.usecase.requests.DecideAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.requests.DecideWorkRequestUseCase
import com.evoapps.evoshift.domain.usecase.requests.SubmitWorkRequestUseCase
import com.evoapps.evoshift.domain.usecase.requests.WorkPlacesUseCase
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate

/**
 * SH-224: the person's requests on their timeline, through SH-223's own flows: read for the
 * visible days only (one query per collection), sent through ReportAbsence / SubmitWorkRequest
 * (the same validation, limits and PENDING state as the person's own form), decided through
 * DecideAbsence / DecideWorkRequest, withdrawn by deleting (SH-223 keeps no cancelled status).
 * firestore.rules decide who may: the person reads their own; the workplace's managers read,
 * send, decide and remove its requests.
 */
class TimelineRequestsUseCase(
    private val absences: AbsenceRepository,
    private val workRequests: WorkRequestRepository,
    private val reportAbsence: ReportAbsenceUseCase,
    private val submitWorkRequest: SubmitWorkRequestUseCase,
    private val decideAbsence: DecideAbsenceUseCase,
    private val decideWorkRequest: DecideWorkRequestUseCase = DecideWorkRequestUseCase(workRequests),
    private val places: WorkPlacesUseCase = WorkPlacesUseCase(workRequests)
) {
    /** [userId]'s requests at [workplaceId] that touch [range]: one query to `absences`, one to `work_requests`. */
    suspend fun load(workplaceId: String, userId: String, range: ClosedRange<LocalDate>, showDeclined: Boolean = false): Result<TimelineRequests> =
        runCatching {
            coroutineScope {
                val from = range.start.toString()
                val to = range.endInclusive.toString()
                val leave = async { absences.getAbsencesOfUserBetween(userId, workplaceId, from, to).getOrThrow() }
                val work = async { workRequests.forUserBetween(userId, workplaceId, from, to).getOrThrow() }
                TimelineRequests.of(leave.await(), work.await(), workplaceId, range, showDeclined)
            }
        }

    /** The workplace's shared places to work from (the form's picker). */
    suspend fun places(workplaceId: String): Result<List<RequestPlace>> = places.list(workplaceId)

    suspend fun addPlace(workplaceId: String, name: String, by: User, existing: List<RequestPlace>): Result<RequestPlace> =
        places.add(workplaceId, name, by.uid, existing)

    /**
     * Sends [draft] for its person, as [by]: PENDING, or approved at once with [approveNow] (a
     * manager who decides requests; SH-223 frees the overlapping shifts and tells the person).
     * Time off in lieu entered by a manager is the person's own ask (1:1), as SH-223 assumes.
     */
    suspend fun create(draft: Absence, by: User, approveNow: Boolean = false): Result<String> =
        reportAbsence.submit(draft, actingUserId = by.uid, approveNow = approveNow)

    suspend fun create(draft: WorkRequest, by: User, approveNow: Boolean = false): Result<String> =
        submitWorkRequest(draft, actingUserId = by.uid, approveNow = approveNow)

    /** Approves or rejects [request] as [by]. An absence over a limit comes back as [AbsenceDecision.OverLimit], unsaved. */
    suspend fun decide(
        request: TimelineRequest,
        status: AbsenceStatus,
        by: User,
        rejectionReason: String? = null,
        overrideNote: String? = null
    ): Result<AbsenceDecision?> {
        request.absence?.let { absence ->
            return decideAbsence(absence, status, by.uid, by.displayName, absence.workplaceId, rejectionReason, overrideNote).map { it }
        }
        val work = request.work ?: return Result.failure(IllegalArgumentException("Not a request"))
        return decideWorkRequest(work, status, by.uid, by.displayName, rejectionReason).map { null }
    }

    /** Withdraws [request] (deleted, as SH-223 withdraws one). */
    suspend fun withdraw(request: TimelineRequest): Result<Unit> {
        request.absence?.let { return absences.deleteAbsence(it.id) }
        val work = request.work ?: return Result.failure(IllegalArgumentException("Not a request"))
        return workRequests.delete(work.id)
    }
}
